import { useResourcesStore } from '@/store/resource';
import { ViewModeKey } from '@/utils/datatable';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import type { DataTableFilterEvent, DataTableSortEvent } from 'primevue/datatable';
import { computed, ref } from 'vue';

/**
 * Composable for managing Resources page state and logic
 * Handles pagination, filtering, sorting for both table and card views
 */
export function useResourcesPage(initialViewMode: 'table' | 'card' = 'table') {
    const store = useResourcesStore();

    // ===== SHARED STATE =====
    const viewMode = ref<'table' | 'card'>(initialViewMode);
    const loading = computed(() => store.loading);
    const resources = computed(() => store.resources);

    // ===== TABLE VIEW STATE =====
    const tablePage = ref(1);
    const tablePageSize = ref(10);
    const tableSortField = ref<string | undefined>('name');
    const tableSortOrder = ref<number>(1);
    const tableMetadata = ref<PagedResultMetadata | null>(null);

    const tableFilters = ref({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        type: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        location: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        enabled: {
            operator: FilterOperator.OR,
            constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
        }
    });

    const tableFirst = computed(() => (tablePage.value - 1) * tablePageSize.value);

    // ===== CARD VIEW STATE =====
    const cardPage = ref(1);
    const cardPageSize = ref(12);
    const cardSearch = ref('');
    const cardStatusFilter = ref<boolean | null>(null);
    const cardMetadata = ref<PagedResultMetadata | null>(null);

    const cardFirst = computed(() => (cardPage.value - 1) * cardPageSize.value);

    // ===== TABLE METHODS =====
    async function fetchTableData() {
        tableMetadata.value = await store.fetchPagedData(
            tablePage.value,
            tablePageSize.value,
            tableSortField.value,
            tableSortOrder.value,
            tableFilters.value
        );
    }

    async function onTablePage(event: { first: number; rows: number; page: number }) {
        tablePage.value = event.page + 1;
        await fetchTableData();
    }

    async function onTableFilter(e: DataTableFilterEvent) {
        const parsedFilters: Record<string, { value: string; matchMode: string } | null> = {};

        for (const key in e.filters) {
            const filter = e.filters[key];
            if (filter && typeof filter === 'object') {
                if ('value' in filter) {
                    if (filter.value != null && filter.value !== '') {
                        parsedFilters[key] = {
                            value: filter.value,
                            matchMode: filter.matchMode ?? 'contains'
                        };
                    } else {
                        parsedFilters[key] = null;
                    }
                } else if ('constraints' in filter && Array.isArray(filter.constraints)) {
                    const first = filter.constraints[0];
                    if (first?.value != null && first.value !== '') {
                        parsedFilters[key] = {
                            value: first.value,
                            matchMode: first.matchMode ?? 'contains'
                        };
                    } else {
                        parsedFilters[key] = null;
                    }
                }
            }
        }

        tablePage.value = 1;
        tableMetadata.value = await store.fetchPagedData(
            tablePage.value,
            tablePageSize.value,
            tableSortField.value,
            tableSortOrder.value,
            parsedFilters
        );
    }

    async function onTableSort(event: DataTableSortEvent) {
        tableSortField.value = (event.sortField as string) || undefined;
        tableSortOrder.value = event.sortOrder as number;
        await fetchTableData();
    }

    function clearTableFilters() {
        tableFilters.value = {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            name: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            type: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
            },
            location: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
            },
            enabled: {
                operator: FilterOperator.OR,
                constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
            }
        };

        tablePage.value = 1;
        fetchTableData();
    }

    // ===== CARD METHODS =====
    async function fetchCardData() {
        const cardFilters: Record<string, any> = {};

        if (cardSearch.value.trim()) {
            cardFilters['name[value]'] = cardSearch.value.trim();
        }

        if (cardStatusFilter.value !== null) {
            cardFilters['enabled[value]'] = cardStatusFilter.value;
            cardFilters['enabled[matchMode]'] = FilterMatchMode.EQUALS;
        }

        cardMetadata.value = await store.fetchPagedData(
            cardPage.value,
            cardPageSize.value,
            'name',
            1,
            cardFilters
        );
    }

    async function onCardPage(event: { first: number; rows: number; page: number }) {
        cardPage.value = event.page + 1;
        await fetchCardData();
    }

    function filterCards() {
        cardPage.value = 1;
        fetchCardData();
    }

    function resetCardFilters() {
        cardSearch.value = '';
        cardStatusFilter.value = null;
        cardPage.value = 1;
    }

    // ===== SHARED METHODS =====
    function toggleView() {
        viewMode.value = viewMode.value === 'table' ? 'card' : 'table';

        // Save preference to localStorage
        localStorage.setItem(ViewModeKey.RESOURCES, viewMode.value);

        if (viewMode.value === 'table') {
            clearTableFilters();
        } else {
            resetCardFilters();
            fetchCardData();
        }
    }

    async function fetchData() {
        if (viewMode.value === 'card') {
            await fetchCardData();
        } else {
            await fetchTableData();
        }
    }

    async function initialize() {
        tablePage.value = 1;
        await fetchData();
    }

    return {
        // State
        viewMode,
        loading,
        resources,

        // Table
        tablePage,
        tablePageSize,
        tableSortField,
        tableSortOrder,
        tableFilters,
        tableMetadata,
        tableFirst,

        // Card
        cardPage,
        cardPageSize,
        cardSearch,
        cardStatusFilter,
        cardMetadata,
        cardFirst,

        // Methods
        toggleView,
        fetchData,
        initialize,

        // Table methods
        fetchTableData,
        onTablePage,
        onTableFilter,
        onTableSort,
        clearTableFilters,

        // Card methods
        fetchCardData,
        onCardPage,
        filterCards,
        resetCardFilters
    };
}
